import React, { Component } from "react";

import Alert from "@cloudscape-design/components/alert";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";

const noneOption = {
	label: "None",
	value: "",
	description: "Do not apply an Affiliate Deposit Ladder",
};

const formatOption = (ladder) => ({
	label: ladder.name,
	value: String(ladder.id),
	description: `ID: ${ladder.id} | ${Number(ladder.active) ? "Enabled" : "Disabled"}`,
});

const getErrorText = (error) => {
	if (!error) {
		return "";
	}

	return error.text || error.message || error.toString();
};

export default class PlayerProfileDepositLadderSection extends Component {
	constructor(props) {
		super(props);
		this.loadRequestId = 0;
		this.unmounted = false;

		this.state = {
			loading: true,
			saving: false,
			options: [noneOption],
			selectedOption: noneOption,
			initialValue: "",
			error: null,
		};
	}

	componentDidMount() {
		this.loadAssignment();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.uid !== this.props.uid) {
			this.loadAssignment();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.loadRequestId += 1;
	}

	loadAssignment = (search = "", updateSelection = true) => {
		if (!Number(this.props.uid)) {
			this.setState({ loading: false });
			return Promise.resolve();
		}

		const requestId = ++this.loadRequestId;
		this.setState({ loading: true, error: null });

		return this.props.Interface.request(
			"get",
			{ class: "data", module: "depositLadder", method: "getAffiliateAssignment" },
			{ uid: this.props.uid, search },
		)
			.then((result) => {
				if (this.unmounted || requestId !== this.loadRequestId) {
					return;
				}

				const options = [noneOption, ...(result.options || []).map(formatOption)];
				const currentOption = result.current ? formatOption(result.current) : noneOption;
				const selectedOption = updateSelection ? currentOption : this.state.selectedOption;

				this.setState({
					loading: false,
					options,
					selectedOption,
					initialValue: updateSelection ? currentOption.value : this.state.initialValue,
					error: null,
				});
			})
			.catch((error) => {
				if (this.unmounted || requestId !== this.loadRequestId) {
					return;
				}

				this.setState({ loading: false, error });
			});
	};

	saveAssignment = () => {
		const { selectedOption } = this.state;

		this.setState({ saving: true, error: null });
		this.props.Interface.request(
			"set",
			{ class: "data", module: "depositLadder", method: "setAffiliateAssignment" },
			{
				uid: this.props.uid,
				ladderId: selectedOption.value ? Number(selectedOption.value) : null,
			},
		)
			.then(() => {
				if (this.unmounted) return;
				this.props.Interface.addNoty("Success", "Deposit Ladder assignment was updated");
				this.setState({ saving: false }, () => this.loadAssignment());
			})
			.catch((error) => {
				if (this.unmounted) return;
				this.setState({ saving: false, error });
			});
	};

	render() {
		const { loading, saving, options, selectedOption, initialValue, error } = this.state;
		const errorText = getErrorText(error);

		return (
			<Container
				header={
					<Header variant="h3" description="Affiliate Deposit Ladder applied to this player's referrals">
						Deposit Ladder
					</Header>
				}>
				<SpaceBetween size="m">
					{Boolean(errorText) && (
						<Alert type="error" header="Failed to load or update Deposit Ladder assignment">
							{errorText}
						</Alert>
					)}
					<FormField label="Affiliate Deposit Ladder">
						<Select
							filteringType="manual"
							onLoadItems={({ detail }) => this.loadAssignment(detail.filteringText, false)}
							selectedOption={selectedOption}
							onChange={({ detail }) => this.setState({ selectedOption: detail.selectedOption })}
							options={options}
							statusType={loading ? "loading" : "finished"}
							loadingText="Loading Deposit Ladders"
							empty="No Affiliate Deposit Ladders found"
							placeholder="Choose Deposit Ladder"
						/>
					</FormField>
					<Button
						variant="primary"
						onClick={this.saveAssignment}
						loading={saving}
						disabled={loading || selectedOption.value === initialValue}>
						Save
					</Button>
				</SpaceBetween>
			</Container>
		);
	}
}
